import type { Channel, ConversationEntry, CustomerRecord } from '@/domain/types';

/**
 * Which send route a DM thread is in (tv-isee), anchored on the customer's last
 * REAL inbound message (a HookChat row) — only their message resets Meta's
 * windows. Threads with no real inbound stay on today's simulated behaviour, so
 * demo data and SMS/email are untouched. The server's 409 is the final word.
 * `nowIso` MUST be WALL time (new Date()), never the app's sim clock: Meta's
 * windows are real time, and a dev/staging sim clock parked in May would put a
 * real September DM "in the future".
 *   < 24h  → reply        (normal send)
 *   < 7d   → human_agent  (HUMAN_AGENT tag; a person must write/review it)
 *   else   → handoff      (copy + open Instagram; recorded unconfirmed)
 */
export type DmSendMode = 'simulated' | 'reply' | 'human_agent' | 'handoff';

const HOUR_MS = 3_600_000;
const REPLY_HOURS = 24;
const HUMAN_AGENT_HOURS = 24 * 7;

/**
 * The anchor for every real-DM window (tv-isee): the customer's last inbound
 * HookChat row. dmSendMode and messengerWindow/windowNowIso share this one
 * predicate so the composer and the inbox chips can never disagree.
 */
export function lastRealInbound(
  customer: Pick<CustomerRecord, 'conversation'>,
): ConversationEntry | undefined {
  return [...(customer.conversation ?? [])]
    .reverse()
    .find((e) => e.direction === 'in' && e.provider === 'hookchat');
}

export function dmSendMode(
  customer: Pick<CustomerRecord, 'conversation'>,
  channel: Channel,
  nowIso: string,
): { mode: DmSendMode; hoursLeft: number } {
  if (channel !== 'ig-dm' && channel !== 'meta-dm') return { mode: 'simulated', hoursLeft: 0 };
  const lastReal = lastRealInbound(customer);
  if (!lastReal) return { mode: 'simulated', hoursLeft: 0 };
  const elapsedH = (Date.parse(nowIso) - Date.parse(lastReal.timestamp)) / HOUR_MS;
  if (elapsedH < REPLY_HOURS) return { mode: 'reply', hoursLeft: REPLY_HOURS - elapsedH };
  if (elapsedH < HUMAN_AGENT_HOURS) return { mode: 'human_agent', hoursLeft: HUMAN_AGENT_HOURS - elapsedH };
  return { mode: 'handoff', hoursLeft: 0 };
}
